import { useCallback, useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { useCreateOrganizationClientMutation } from "@api/organizationClient.api";
import { useEntityCreationMutation } from "@api/entity.api";
import { EntityProfileType } from "@common/types/entity";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import { formatISO, startOfYear, subDays } from "date-fns";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useConstant } from "@hooks/useConstant";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { CoaSetupBody } from "@common/schemas/coa-setup.schema";
import { CoaSetupMethod } from "@common/constants/domains/accounting/coaSetup";
import {
    AddClientSteps,
    AddClientMode,
    ClientFormSchema,
    addClientFormSchema,
    inviteStepSchema,
} from "./AddClientSteps/lib";

interface UseAddClientFormArgs {
    onClose: () => void;
    onSuccess?: (
        clientId: string,
        values: ClientFormSchema,
        invitedCount: number,
    ) => void;
    multiEntityFlow: boolean;
    accountingConnection?: IntegrationConnection | null;
    mode?: AddClientMode;
    clientId?: string;
    resetKey?: number;
}

function buildMigrationPayload(
    values: Pick<
        ClientFormSchema,
        | "historicalData"
        | "migrationMethod"
        | "syncChartOfAccounts"
        | "shouldUseExternalChartOfAccounts"
    >,
    accountingConnection: IntegrationConnection | null | undefined,
) {
    const {
        historicalData,
        migrationMethod,
        syncChartOfAccounts,
        shouldUseExternalChartOfAccounts,
    } = values;

    const wantsMigration =
        historicalData.migrate || !!migrationMethod || syncChartOfAccounts;

    if (!wantsMigration) {
        return undefined;
    }

    return {
        externalSystem: migrationMethod,
        syncChartOfAccounts:
            syncChartOfAccounts || historicalData.syncGeneralLedger,
        shouldUseExternalChartOfAccounts,
        syncGeneralLedger: historicalData.syncGeneralLedger,
        syncCounterparties: historicalData.syncCounterparties,
        syncAttachments: historicalData.syncAttachments,
        syncClasses: historicalData.syncClasses,
        syncCatalogItems: historicalData.syncCatalogItems,
        syncInvoices: historicalData.syncInvoices,
        syncBills: historicalData.syncBills,
        startDate: historicalData.startDate,
        integrationConnectionId: accountingConnection?.id,
    };
}

function buildCoaSetupPayload(
    values: Pick<
        ClientFormSchema,
        "coaSetupMethod" | "coaTemplateId" | "coaSourceEntityId"
    >,
): CoaSetupBody | undefined {
    const { coaSetupMethod, coaTemplateId, coaSourceEntityId } = values;

    if (coaSetupMethod === "template" && coaTemplateId !== null) {
        return { method: CoaSetupMethod.template, templateId: coaTemplateId };
    }

    if (coaSetupMethod === "entity" && coaSourceEntityId !== null) {
        return {
            method: CoaSetupMethod.entity,
            sourceEntityId: coaSourceEntityId,
        };
    }

    return undefined;
}

export function useAddClientForm({
    onClose,
    onSuccess,
    multiEntityFlow,
    accountingConnection,
    mode = "newClient",
    clientId,
    resetKey,
}: UseAddClientFormArgs) {
    const [loadingAction, setLoadingAction] = useState<
        "invite" | "skip" | null
    >(null);
    const [currentStep, setCurrentStep] = useState(AddClientSteps.CLIENT);
    const activeOrganization = useActiveOrganization();
    const { newAddClientFlow } = useTypedFlags();
    const createOrganizationClientMutation =
        useCreateOrganizationClientMutation();
    const createEntityMutation = useEntityCreationMutation();
    const { toast } = useToaster();

    const handleStepChange = useCallback((stepId: AddClientSteps) => {
        setCurrentStep(stepId);
    }, []);

    const defaultBookkeepingStartDate = useConstant(() =>
        formatISO(startOfYear(new Date()), {
            representation: "date",
        }),
    );
    const defaultMigrationEndDate = useConstant(() =>
        formatISO(subDays(startOfYear(new Date()), 1), {
            representation: "date",
        }),
    );

    const formik = useFormik<ClientFormSchema>({
        initialValues: {
            mode,
            multiEntityFlow,
            isMultiEntity: multiEntityFlow ? null : false,
            client: {
                clientName: "",
                businessName: "",
                bookkeepingStartDate: defaultBookkeepingStartDate,
                legalType: EntityProfileType.SOLE_PROPRIETORSHIP,
                servicesOffered:
                    activeOrganization?.profile?.servicesOffered ?? [],
                industries: [],
                industryOther: "",
            },
            migrationMethod: null,
            syncChartOfAccounts: false,
            shouldUseExternalChartOfAccounts: false,
            coaSetupMethod: "kick",
            coaTemplateId: null,
            coaSourceEntityId: null,
            isAccrualLedgerEnabled: false,
            historicalData: {
                migrate: newAddClientFlow,
                startDate: undefined,
                endDate: defaultMigrationEndDate,
                syncGeneralLedger: true,
                syncCounterparties: newAddClientFlow,
                syncAttachments: newAddClientFlow,
                syncClasses: newAddClientFlow,
                syncCatalogItems: newAddClientFlow,
                syncInvoices: newAddClientFlow,
                syncBills: newAddClientFlow,
            },
            plan: {
                billingOwnership: true,
                plan: FLAT_RATE_PLANS.PLUS,
            },
            invite: {
                emails: [],
                role: UserWorkspaceRole.ADMIN,
            },
            billingTransfer: {
                email: "",
            },
        },
        enableReinitialize: true,
        validate: validateFormikWithZodSchema(addClientFormSchema),
        onSubmit: submitHelper({
            loading: loadingAction === "invite",
            setLoading: (value: boolean) =>
                setLoadingAction(value ? "invite" : null),
            handler: async (formValues) => {
                const {
                    client,
                    plan,
                    invite,
                    billingTransfer,
                    isAccrualLedgerEnabled,
                } = formValues;

                const coaSetup = buildCoaSetupPayload(formValues);
                const migration = buildMigrationPayload(
                    formValues,
                    accountingConnection,
                );

                if (mode === "addEntity") {
                    if (!clientId) {
                        throw new Error(
                            "Add entity mode requires an existing client workspace id",
                        );
                    }

                    await createEntityMutation.mutateAsync({
                        workspaceId: clientId,
                        name: client.businessName,
                        legalType: client.legalType,
                        bookkeepingStartDate: client.bookkeepingStartDate,
                        servicesOffered: client.servicesOffered,
                        isAccrualLedgerEnabled,
                        migration,
                        coaSetup,
                        profile: {
                            industries: client.industries,
                            industryOther: client.industryOther,
                        },
                    });

                    toast("Entity added");
                    onSuccess?.(clientId, formValues, 0);
                    return;
                }

                if (!activeOrganization) {
                    return;
                }

                const inviteParseResult = inviteStepSchema.safeParse(invite);
                const invitedCount = inviteParseResult.success
                    ? invite.emails.length
                    : 0;

                const createdClientId =
                    await createOrganizationClientMutation.mutateAsync({
                        organizationId: activeOrganization.id,
                        payload: {
                            workspaceName: client.clientName,
                            entity: {
                                name: client.businessName,
                                legalType: client.legalType,
                                industries: client.industries,
                                industryOther: client.industryOther,
                            },
                            bookkeepingStartDate: client.bookkeepingStartDate,
                            servicesOffered: client.servicesOffered,
                            plan: plan.plan,
                            inviteClient: inviteParseResult.success
                                ? invite
                                : undefined,
                            billingOwnership: plan.billingOwnership,
                            billingTransfer: plan.billingOwnership
                                ? undefined
                                : billingTransfer,
                            isAccrualLedgerEnabled,
                            migration,
                            coaSetup,
                        },
                    });

                toast("Client added");
                if (onSuccess) {
                    onSuccess(createdClientId, formValues, invitedCount);
                } else {
                    onClose();
                }
            },
        }),
    });

    const valuesRef = useRef(formik.values);
    valuesRef.current = formik.values;
    const resetForm = formik.resetForm;

    useEffect(() => {
        resetForm();
    }, [resetKey, resetForm]);

    const submitWithoutInvite = useCallback(async () => {
        if (!activeOrganization) {
            return;
        }

        const currentValues = valuesRef.current;
        const { client, plan, isAccrualLedgerEnabled } = currentValues;
        const coaSetup = buildCoaSetupPayload(currentValues);

        const createClient = async () => {
            setLoadingAction("skip");

            const createdClientId =
                await createOrganizationClientMutation.mutateAsync({
                    organizationId: activeOrganization.id,
                    payload: {
                        workspaceName: client.clientName,
                        entity: {
                            name: client.businessName,
                            legalType: client.legalType,
                            industries: client.industries,
                            industryOther: client.industryOther,
                        },
                        bookkeepingStartDate: client.bookkeepingStartDate,
                        servicesOffered: client.servicesOffered,
                        plan: plan.plan,
                        billingOwnership: plan.billingOwnership,
                        isAccrualLedgerEnabled,
                        migration: buildMigrationPayload(
                            currentValues,
                            accountingConnection,
                        ),
                        coaSetup,
                    },
                });
            toast("Client added");
            if (onSuccess) {
                onSuccess(createdClientId, currentValues, 0);
            } else {
                onClose();
            }
        };
        await createClient().finally(() => {
            setLoadingAction(null);
        });
    }, [
        accountingConnection,
        activeOrganization,
        createOrganizationClientMutation,
        onClose,
        onSuccess,
        toast,
    ]);

    return {
        formik,
        loadingAction,
        currentStep,
        handleStepChange,
        submitWithoutInvite,
    };
}
